import { useState } from "react";
import { useTranslation } from "react-i18next";
import { umamiEvent } from "../lib/analytics";

const dismissalKey = "hideGeoBingoRecommendation";

interface GeoBingoCardProps {
  isDark: boolean;
  variant: "game" | "daily" | "openlobby";
}

export function GeoBingoCard({ isDark, variant }: GeoBingoCardProps) {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(
    () =>
      typeof window !== "undefined" &&
      window.localStorage.getItem(dismissalKey) === "1",
  );
  const isDaily = variant === "daily";
  const href = isDaily
    ? "https://geobingo.io/?utm_source=wikiparty&utm_medium=referral&utm_campaign=cross_game&utm_content=daily_results"
    : "https://geobingo.io/?utm_source=wikiparty&utm_medium=referral&utm_campaign=cross_game&utm_content=multiplayer_results";

  if (dismissed) return null;

  const dismiss = () => {
    window.localStorage.setItem(dismissalKey, "1");
    setDismissed(true);
  };

  return (
    <aside
      data-testid="geobingo-card"
      className={`rounded-xl border p-2.5 sm:px-4 sm:py-2.5 ${
        isDark
          ? "border-zinc-800 bg-zinc-950/50"
          : "border-gray-200 bg-gray-50/50"
      }`}
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <h2
            className={`text-sm font-medium sm:text-base ${isDark ? "text-white" : "text-gray-900"}`}
          >
            <span
              className={`mr-1 font-normal ${isDark ? "text-zinc-500" : "text-gray-500"}`}
            >
              {t("crossGame.label")}:
            </span>
            {isDaily ? t("crossGame.dailyTitle") : "GeoBingo"}
          </h2>
          <p
            className={`text-xs sm:text-sm ${isDark ? "text-zinc-500" : "text-gray-500"}`}
          >
            {isDaily
              ? t("crossGame.dailyDescription")
              : t("crossGame.description")}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <a
            data-testid="geobingo-link"
            href={href}
            {...umamiEvent("geobingo-try", { variant })}
            className={`flex-1 rounded-md px-3 py-1.5 text-center text-xs font-medium transition-colors ${
              isDark
                ? "bg-white text-black hover:bg-zinc-200"
                : "bg-gray-900 text-white hover:bg-gray-800"
            }`}
          >
            {isDaily ? t("crossGame.dailyAction") : t("crossGame.action")}
          </a>
          <button
            type="button"
            data-testid="dismiss-geobingo-card"
            onClick={dismiss}
            aria-label={t("crossGame.dismiss")}
            title={t("crossGame.dismiss")}
            className={`flex h-7 w-7 cursor-pointer items-center justify-center rounded-md border text-base transition-colors ${
              isDark
                ? "border-zinc-800 text-zinc-500 hover:bg-zinc-900 hover:text-white"
                : "border-gray-200 text-gray-400 hover:bg-gray-100 hover:text-gray-900"
            }`}
          >
            ×
          </button>
        </div>
      </div>
    </aside>
  );
}
